/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, {
    forwardRef, Suspense, useCallback,
    useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import {
    getValidationRules, Validate, ValidateAll,
    ValidateFileField, ValidateImageField,
} from '@repo/design-system/components/forms/validation';
import FormFieldsControl from './FormFieldsControl';
import { useTranslation } from "react-i18next";
import FormLoader from './formsUi/formLoader/FormLoader';

const DEFAULT_VALIDATION_STATE = {
    isValid: true,
    error: {},
};

const FormBuilderComponent = forwardRef((props, ref) => {

    // Props
    const { initialState, config, onFormDataChange, onColumnChange,
        onFieldBlur, onActionChange, ...restProps } = props;

    const { formFields, section, formSetting,
        initialState: configInitialState, } = config;


    //  State
    const { t } = useTranslation();
    const [formData, setFormData] = useState(initialState || {});
    const [validationState, setValidationState] = useState(
        DEFAULT_VALIDATION_STATE
    );

    // Memoized Functions
    const validationRules = useMemo(
        () => getValidationRules(section, t),
        [section, t]
    );

    // Initial State 
    useEffect(() => {
        setFormData(configInitialState || {});
        setValidationState(DEFAULT_VALIDATION_STATE);
    }, [configInitialState]);

    // Validation
    const validateForm = useCallback(() => {
        const result = ValidateAll(formData, validationRules, config);
        setValidationState(result);
        return result;
    }, [formData, validationRules]);

    const validateField = useCallback((fieldName) => {
        if (!validationRules[fieldName]) return;

        const result = Validate(formData, validationRules, fieldName);

        setValidationState((prev) => {
            const nextErrors = { ...prev.error };

            if (!result.isValid) {
                nextErrors[fieldName] = result.error[fieldName];
            } else {
                delete nextErrors[fieldName];
            }

            return {
                isValid: Object.keys(nextErrors).length === 0,
                error: nextErrors,
            };
        });
    }, [formData, validationRules]);

    // File Validation
    const validateFileField = useCallback((files, fieldName, fileConfig, updatedData) => {
        if (!Array.isArray(files) || !validationRules[fieldName]) return;

        setValidationState((prev) => {
            const nextErrors = { ...prev.error };

            files.forEach((file) => {
                const result = ValidateFileField(
                    file.file,
                    validationRules[fieldName],
                    updatedData || formData,
                    fileConfig?.acceptedFileFormats,
                    fileConfig?.minFileSizeInMB,
                    fileConfig?.maxFileSizeInMB,
                    fieldName
                );

                if (!result.isValid) {
                    nextErrors[fieldName] = result.messages[0]?.message;
                } else {
                    delete nextErrors[fieldName];
                }
            });

            return {
                isValid: Object.keys(nextErrors).length === 0,
                error: nextErrors,
            };
        });
    },
        [formData, validationRules]
    );

    // Image Validation 
    const validateImageField = useCallback((files, fieldName, imageConfig, updatedData) => {
        if (!Array.isArray(files) || !validationRules[fieldName]) return;

        setValidationState((prev) => {
            const nextErrors = { ...prev.error };

            files.forEach((file) => {
                const result = ValidateImageField(
                    file.file,
                    validationRules[fieldName],
                    updatedData || formData,
                    imageConfig,
                    file.image
                );

                if (!result.isValid) {
                    nextErrors[fieldName] = result.messages[0]?.message;
                } else {
                    delete nextErrors[fieldName];
                }
            });

            return {
                isValid: Object.keys(nextErrors).length === 0,
                error: nextErrors,
            };
        });
    },
        [formData, validationRules]
    );

    //  State Change
    const handleFormDataChange = useCallback((updatedData) => {
        setFormData((prev) => ({
            ...prev,
            ...updatedData
        }));
        onFormDataChange?.(updatedData);
    }, [onFormDataChange]);

    // Imperative Handler
    useImperativeHandle(ref, () => ({
        getValidatedFormData() {
            const result = validateForm();
            return result.isValid ? formData : null;
        },
        getFormData() {
            return formData;
        },
    }));


    if (!section) {
        return <div>No fields configured</div>;
    }

    return (
        <Suspense fallback={<div>Loading form...</div>}>
            {props.loading ? <FormLoader /> :
                <FormFieldsControl
                    sections={section}
                    fields={formFields}
                    formData={formData}
                    validationState={validationState}
                    onFormStateChange={handleFormDataChange}
                    onFieldValidate={validateField}
                    onFileValidate={validateFileField}
                    onImageValidate={validateImageField}
                    onFieldBlur={onFieldBlur}
                    onFormFieldChange={onColumnChange}
                    onActionChange={onActionChange}
                    formSetting={formSetting}
                    validationRules={validationRules}
                    {...restProps}
                />
            }
        </Suspense>
    );
});

FormBuilderComponent.propTypes = {
    initialState: PropTypes.object,
    config: PropTypes.object.isRequired,
    onFormDataChange: PropTypes.func,
    onColumnChange: PropTypes.func,
    onFieldBlur: PropTypes.func,
    onActionChange: PropTypes.func,
};

export const FormBuilder = React.memo(FormBuilderComponent);